import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BellRing, CalendarClock, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { DashboardAPI } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const REFRESH_INTERVAL_MS = 60_000;

export function ServiceRemindersTile({ className }) {
    const { permissions } = useAuth();
    const canSee = (permissions || []).includes("reminder.read");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!canSee) return;
        let alive = true;
        DashboardAPI.remindersSummary()
            .then((d) => alive && setData(d))
            .catch(() => {})
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [canSee, tick]);

    useEffect(() => {
        if (!canSee) return;
        const id = setInterval(() => setTick((t) => t + 1), REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [canSee]);

    if (!canSee) return null;

    const overdue = data?.overdue ?? 0;
    const dueThisWeek = data?.due_this_week ?? 0;
    const scheduled = data?.scheduled ?? 0;
    const dispatched = data?.dispatched_today ?? 0;
    const responded = data?.responded_today ?? 0;

    return (
        <Card
            data-testid="service-reminders-tile"
            className={cn(
                "group relative overflow-hidden border-l-4 border-blue-500 p-4 transition-shadow hover:shadow-md",
                className,
            )}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-400">
                        <BellRing className="h-3 w-3" />
                        Service reminders
                    </div>
                    {loading ? (
                        <div className="mt-2 flex h-12 items-center">
                            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
                        </div>
                    ) : (
                        <div className="mt-2 grid grid-cols-3 gap-3">
                            <div data-testid="reminders-tile-overdue">
                                <div
                                    className={cn(
                                        "font-display text-2xl font-bold tabular-nums",
                                        overdue > 0
                                            ? "text-rose-600 dark:text-rose-400"
                                            : "text-zinc-900 dark:text-zinc-50",
                                    )}
                                >
                                    {overdue}
                                </div>
                                <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    {overdue > 0 ? <AlertTriangle className="h-3 w-3 text-rose-500" /> : null}
                                    Overdue
                                </div>
                            </div>
                            <div data-testid="reminders-tile-thisweek">
                                <div className="font-display text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                                    {dueThisWeek}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    Due this week
                                </div>
                            </div>
                            <div data-testid="reminders-tile-scheduled">
                                <div className="font-display text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                                    {scheduled}
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    Active
                                </div>
                            </div>
                        </div>
                    )}
                    <p
                        className="mt-3 text-xs text-zinc-500"
                        data-testid="reminders-tile-footer"
                    >
                        {dispatched} dispatched today
                        {responded > 0 ? (
                            <>
                                {" · "}
                                <span
                                    data-testid="reminders-tile-responded"
                                    className="font-medium text-emerald-700 dark:text-emerald-400"
                                >
                                    {responded} responded
                                </span>
                            </>
                        ) : null}
                        {" · "}
                        {data?.window?.timezone || ""}
                    </p>
                </div>
                <CalendarClock className="h-5 w-5 shrink-0 text-blue-500" />
            </div>
            <Link
                to="/reminders"
                data-testid="reminders-tile-open"
                className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-blue-700 hover:underline dark:text-blue-400"
            >
                Open reminders →
            </Link>
        </Card>
    );
}
